import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { parse } from 'query-string';
import { always, omit } from 'ramda';
import { useRouter } from 'next/router';
import { AnimatePresence } from 'framer-motion';

import { DIRECTION_TYPES, SORT_TYPES } from '@definitions/artworks.types';
import reportError from '@utils/reportError';
import { renderWhenTrue } from '@utils/rendering';

import { Container, Content, SortButton, DirectionArrow, SortButtonsWrapper } from './sortDropdown.styles';

export interface SortDropdownProps {
  isOpen: boolean;
}

export const SortDropdown = ({ isOpen }: SortDropdownProps) => {
  const router = useRouter();
  const { sort: querySort, direction: queryDirection } = router.query as {
    sort: SORT_TYPES;
    direction: DIRECTION_TYPES;
  };
  const [direction, setDirection] = useState<DIRECTION_TYPES>(queryDirection || DIRECTION_TYPES.ASC);
  const [sort, setSort] = useState<SORT_TYPES | null>(querySort || null);
  const handleChangeSort = ({
    direction = DIRECTION_TYPES.ASC,
    sort = null,
  }: {
    direction?: DIRECTION_TYPES;
    sort?: SORT_TYPES | null;
  }) => async () => {
    try {
      const query = parse(location.search.replace('?', ''));
      const sortOptions = sort ? { direction, sort } : {};
      setDirection(direction);
      setSort(sort);
      window.scrollTo(0, 0);

      await router.push(
        {
          pathname: router.pathname,
          query: { ...sortOptions, ...omit(['direction', 'sort'], query) },
        },
        undefined,
        {
          shallow: true,
        }
      );
    } catch (e) {
      reportError(e);
    }
  };

  const renderContent = renderWhenTrue(
    always(
      <Content
        key="sortDropdown"
        initial={{ y: '-100%' }}
        animate={{ y: 0, transition: { duration: 0.6 } }}
        exit={{ y: '-100%', transition: { duration: 0.5 } }}
        transition={{ ease: 'ease-in-out' }}
      >
        <SortButtonsWrapper>
          <SortButton active={!sort} onClick={handleChangeSort({})} autoFocus>
            <FormattedMessage id="sortDropdown.default" defaultMessage="Default" />
          </SortButton>
        </SortButtonsWrapper>
        <SortButtonsWrapper>
          <SortButton
            onClick={handleChangeSort({
              sort: SORT_TYPES.PRICE,
              direction:
                direction === DIRECTION_TYPES.ASC && sort === SORT_TYPES.PRICE
                  ? DIRECTION_TYPES.DESC
                  : DIRECTION_TYPES.ASC,
            })}
          >
            <FormattedMessage id="sortDropdown.price" defaultMessage="Price" />
          </SortButton>
          <DirectionArrow
            onClick={handleChangeSort({ sort: SORT_TYPES.PRICE, direction: DIRECTION_TYPES.ASC })}
            active={sort === SORT_TYPES.PRICE && direction == DIRECTION_TYPES.ASC}
          >
            ↑
          </DirectionArrow>
          <DirectionArrow
            onClick={handleChangeSort({ sort: SORT_TYPES.PRICE, direction: DIRECTION_TYPES.DESC })}
            active={sort === SORT_TYPES.PRICE && direction == DIRECTION_TYPES.DESC}
          >
            ↓
          </DirectionArrow>
        </SortButtonsWrapper>
        <SortButtonsWrapper>
          <SortButton
            onClick={handleChangeSort({
              sort: SORT_TYPES.SIZE,
              direction:
                direction === DIRECTION_TYPES.ASC && sort === SORT_TYPES.SIZE
                  ? DIRECTION_TYPES.DESC
                  : DIRECTION_TYPES.ASC,
            })}
          >
            <FormattedMessage id="sortDropdown.size" defaultMessage="Size" />
          </SortButton>
          <DirectionArrow
            onClick={handleChangeSort({ sort: SORT_TYPES.SIZE, direction: DIRECTION_TYPES.ASC })}
            active={sort === SORT_TYPES.SIZE && direction == DIRECTION_TYPES.ASC}
          >
            ↑
          </DirectionArrow>
          <DirectionArrow
            onClick={handleChangeSort({ sort: SORT_TYPES.SIZE, direction: DIRECTION_TYPES.DESC })}
            active={sort === SORT_TYPES.SIZE && direction == DIRECTION_TYPES.DESC}
          >
            ↓
          </DirectionArrow>
        </SortButtonsWrapper>
      </Content>
    )
  );

  return (
    <Container isOpen={isOpen}>
      <AnimatePresence>{renderContent(isOpen)}</AnimatePresence>
    </Container>
  );
};
